import React, { useEffect, useState } from "react";
import { useFetchApi } from "../../../../../hooks/useFetchApi";
import { Card, Table, Spin, Tag, Typography } from "antd";
import { EpsChartline } from "./components/EpsChartline";

const { Text } = Typography;

interface PeersTabProps {
  sectorPeers: any[];
  baseCurrency: string;
  selectedStock: any;
}

export const PeersTab: React.FC<PeersTabProps> = ({
  sectorPeers,
  baseCurrency,
  selectedStock,
}) => {
  const [peersData, setPeersData] = useState<any[]>([]);

  const { response, loading, execute } = useFetchApi("/api/peers", {}, "POST");

  useEffect(() => {
    if (!sectorPeers?.length) return;

    execute({
      stocks: sectorPeers
        .filter((s) => s.status === 1)
        .map((s) => ({ id: s.id })),
      baseCurrency,
    });
  }, [sectorPeers, baseCurrency]);

  useEffect(() => {
    if (response) setPeersData(response?.data);
  }, [response]);

  // Sort so current stock is always first
  const sortedPeersData = [...peersData].sort((a, b) => {
    if (a.id === selectedStock?.id) return -1;
    if (b.id === selectedStock?.id) return 1;
    return 0;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <Spin size="large" />
      </div>
    );
  }

  if (!sectorPeers?.length) {
    return (
      <div className="flex justify-center items-center py-16 text-gray-400">
        No sector peers found for this stock.
      </div>
    );
  }

  const columns = [
    {
      title: "COMPANY",
      dataIndex: "id",
      key: "company",
      render: (id: number) => {
        const peer = sectorPeers.find((s) => s.id === id);
        const isCurrent = id === selectedStock?.id;
        return (
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-1">
              <Text strong>{peer?.ticker_symbol ?? id}</Text>
              <Tag
                color={isCurrent ? "blue" : "default"}
                className="text-[10px] leading-none m-0"
              >
                {isCurrent ? "CURRENT" : "PEER"}
              </Tag>
            </div>
            <Text type="secondary" className="text-xs">
              {peer?.company_name}
            </Text>
          </div>
        );
      },
    },
    {
      title: (
        <>
          EPS GROWTH
          <br />
          (CURRENT VS PAST)
        </>
      ),
      key: "epsGrowth",

      render: (_: any, record: any) => {
        const epsGrowthData = record?.data?.epsGrowthCurrentVsPast ?? null;
        return <EpsChartline epsGrowthData={epsGrowthData} />;
      },
    },
    {
      title: "P. / CHG(D)%",
      key: "price",
      render: (_: any, record: any) => {
        const peer = sectorPeers.find((s) => s.id === record.id);
        return peer ? (
          <div className="flex flex-col">
            <Text strong>
              {baseCurrency} {peer.closed?.toFixed(2)}
            </Text>
            <Text
              strong
              style={{ color: peer.change_perc >= 0 ? "#16a34a" : "#dc2626" }}
            >
              {peer.change_perc >= 0 ? "+" : ""}
              {peer.change_perc?.toFixed(2)}%
            </Text>
          </div>
        ) : (
          "-"
        );
      },
    },
    {
      title: "CAPITAL",
      key: "marketCap",
      render: (_: any, record: any) => {
        const marketCap = record?.data?.dynamic?.marketCap;
        if (marketCap == null) return <Text type="secondary">-</Text>;

        const num = Number(marketCap);
        let formatted: string;

        if (num >= 1_000_000_000) {
          formatted = `${baseCurrency}. ${(num / 1_000_000_000).toFixed(2)} B`;
        } else if (num >= 1_000_000) {
          formatted = `${baseCurrency} ${(num / 1_000_000).toFixed(2)} M`;
        } else {
          formatted = `${baseCurrency}. ${num.toLocaleString("en-US", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })}`;
        }

        return <Text>M.cap: {formatted}</Text>;
      },
    },
    {
      title: "PROFITS (LTM)",
      key: "profits",
      render: (_: any, record: any) => {
        const dynamic = record?.data?.dynamic;
        const metrics = [
          { label: "ROE", value: dynamic?.returnOnEquityLTM },
          { label: "Gross margin", value: dynamic?.grossMarginLTM },
          { label: "Net margin", value: dynamic?.netMarginLTM },
        ];

        return (
          <div className="flex flex-col gap-0.5">
            {metrics.map(({ label, value }) => (
              <Text key={label} className="text-xs">
                <Text type="secondary" className="text-xs">
                  {label}:{" "}
                </Text>
                {value != null && isFinite(value)
                  ? `${Number(value).toFixed(1)}%`
                  : "-"}
              </Text>
            ))}
          </div>
        );
      },
    },
    {
      title: "MULTIPLE",
      key: "multiple",
      render: (_: any, record: any) => {
        // PELTM is -1 when LTM EPS is zero or negative (P/E not meaningful).
        const pe = record?.data?.dynamic?.PELTM;
        return (
          <Text className="text-xs">
            <Text type="secondary" className="text-xs">
              P/E:{" "}
            </Text>
            {pe != null && pe > 0 ? `${Number(pe).toFixed(2)}x` : "-"}
          </Text>
        );
      },
    },
  ];

  return (
    <div className="p-4">
      <Card
        className="w-full shadow-sm border border-gray-200 rounded-xl vi-glass-card"
        styles={{ body: { padding: 0 } }}
      >
        <Table
          rowKey="id"
          columns={columns}
          dataSource={sortedPeersData}
          pagination={false}
          size="small"
          bordered={false}
          className="vi-peers-table"
          scroll={{ x: Math.max(900, columns.length * 150), y: 600 }}
          rowClassName={(record: any) =>
            record.id === selectedStock?.id ? "current-stock-row" : ""
          }
        />
      </Card>
    </div>
  );
};
